import { getLong, logicalKeys, removeLong, setLong, type Storage } from '../platform/storage'
import { KEY, NOTES_PREFIX, yearMonthOfEpochDay } from './keys'

/** Заметка о дне. Формат совпадает с Android (`NotesStore`). */
export interface Note {
  id: number
  /** День, к которому относится заметка, в виде epochDay. */
  day: number
  /** Когда произошло событие, миллисекунды. */
  time: number
  tag: NoteTag
  text: string
  /**
   * Когда заметку последний раз меняли. По этой метке сервер решает, чья запись свежее:
   * без неё правка текста не доезжала — время события не менялось, и сервер считал,
   * что нового нет.
   */
  updatedAt?: number
  /**
   * Заметку удалили. Запись остаётся на устройстве меткой: иначе сервер о удалении
   * не узнаёт и присылает заметку обратно при следующем обмене.
   */
  deleted?: boolean
}

export type NoteTag = 'FIRST' | 'SLEEP' | 'TEETH' | 'DOCTOR'
const TAGS: NoteTag[] = ['FIRST', 'SLEEP', 'TEETH', 'DOCTOR']

/**
 * Заметки хранятся помесячно: `notes_2026_09`. Календарь и так показывает один месяц,
 * а Telegram CloudStorage не принимает больше 4 КБ на значение (длинные месяцы режутся на части).
 */

function parseMonth(raw: string | null): Note[] {
  if (raw === null) return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((n) => typeof n?.id === 'number' && typeof n?.day === 'number' && typeof n?.time === 'number')
      .map((n) => ({
        id: n.id,
        day: n.day,
        time: n.time,
        tag: TAGS.includes(n.tag) ? (n.tag as NoteTag) : 'FIRST',
        text: typeof n.text === 'string' ? n.text : '',
        ...(typeof n.updatedAt === 'number' ? { updatedAt: n.updatedAt } : {}),
        ...(n.deleted === true ? { deleted: true } : {}),
      }))
  } catch {
    return []
  }
}

export async function loadMonth(storage: Storage, yearMonth: string): Promise<Note[]> {
  return parseMonth(await getLong(storage, KEY.notesMonth(yearMonth)))
}

/** Всё, что лежит на устройстве, включая метки об удалении: это нужно обмену. */
export async function loadAllNotes(storage: Storage): Promise<Note[]> {
  const months = logicalKeys(await storage.keys()).filter((k) => k.startsWith(NOTES_PREFIX))
  const all: Note[] = []
  for (const key of months) all.push(...parseMonth(await getLong(storage, key)))
  return all.sort((a, b) => a.time - b.time)
}

/** Живые заметки: то, что показываем человеку. */
export async function loadNotes(storage: Storage): Promise<Note[]> {
  return (await loadAllNotes(storage)).filter((n) => n.deleted !== true)
}

async function writeMonth(storage: Storage, yearMonth: string, notes: Note[]): Promise<void> {
  const key = KEY.notesMonth(yearMonth)
  if (notes.length === 0) await removeLong(storage, key)
  else await setLong(storage, key, JSON.stringify(notes.sort((a, b) => a.time - b.time)))
}

export async function addNote(storage: Storage, note: Note): Promise<void> {
  const month = yearMonthOfEpochDay(note.day)
  await writeMonth(storage, month, [...(await loadMonth(storage, month)), note])
}

export async function updateNote(storage: Storage, note: Note): Promise<void> {
  const month = yearMonthOfEpochDay(note.day)
  const existing = await loadMonth(storage, month)
  const updated = existing.some((n) => n.id === note.id)
    ? existing.map((n) => (n.id === note.id ? note : n))
    : [...existing, note]
  await writeMonth(storage, month, updated)
}

/**
 * Удаление — это тоже запись. Раньше заметка просто пропадала с устройства, сервер
 * о том не узнавал и присылал её обратно: удалённое воскресало при следующем обмене.
 */
export async function removeNote(storage: Storage, note: Note): Promise<void> {
  const month = yearMonthOfEpochDay(note.day)
  const метка: Note = { ...note, deleted: true, updatedAt: Date.now() }
  await writeMonth(
    storage,
    month,
    (await loadMonth(storage, month)).map((n) => (n.id === note.id ? метка : n)).concat(
      (await loadMonth(storage, month)).some((n) => n.id === note.id) ? [] : [метка],
    ),
  )
}
